import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Simple Head Manager Component
 * A lightweight alternative to react-helmet that directly manipulates document head
 */
const HeadManager = ({ title, meta = [], children }) => {
  useEffect(() => {
    // Update document title if provided
    if (title) {
      // Ensure title format is always "Sanoma Games - Page Title"
      if (title === "Sanoma Games") {
        document.title = title;
      } else if (title.startsWith("Sanoma Games - ")) {
        document.title = title;
      } else {
        document.title = `Sanoma Games - ${title}`;
      }
    }
    
    // Handle meta tags
    if (meta && meta.length > 0) {
      // Create and update meta tags
      meta.forEach(metaItem => {
        // Check if meta tag already exists with the name or property
        const selector = metaItem.name 
          ? `meta[name="${metaItem.name}"]` 
          : metaItem.property 
            ? `meta[property="${metaItem.property}"]` 
            : null;
        
        if (selector) {
          // Find existing meta tag
          let existingMeta = document.querySelector(selector);
          
          // If tag exists, update content, otherwise create new
          if (existingMeta) {
            existingMeta.setAttribute('content', metaItem.content);
          } else {
            const newMeta = document.createElement('meta');
            
            // Set attributes based on what's provided
            if (metaItem.name) newMeta.setAttribute('name', metaItem.name);
            if (metaItem.property) newMeta.setAttribute('property', metaItem.property);
            if (metaItem.content) newMeta.setAttribute('content', metaItem.content);
            
            // Add new meta tag to head
            document.head.appendChild(newMeta);
          }
        }
      });
    }
    
    // Clean up function to remove meta tags when component unmounts
    return () => {
      // Meta tags don't need cleanup as they will be updated or reused
    };
  }, [title, meta]);
  
  // This component doesn't render anything visible
  return <>{children}</>;
};

HeadManager.propTypes = {
  title: PropTypes.string,
  meta: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      property: PropTypes.string,
      content: PropTypes.string.isRequired
    })
  ),
  children: PropTypes.node
};

export default HeadManager;